// Hierarchical option list for account <select> dropdowns.
//
// The default "sort by code" doesn't tell you which "Original Cost" belongs to
// which fixed-asset group — Signage's Original Cost and Vehicles's Original
// Cost both just read "Original Cost". This helper:
//   1. Walks the full parent chain so the displayed label is "Parent:Child"
//      (e.g. "Signage:Original Cost") even when the parent header is filtered
//      out of the dropdown (the common case — header accounts have
//      allowDirectPosting=false).
//   2. Sorts pre-order through the full tree so siblings stay grouped under
//      their parent. Within a sibling level, sorts by code (numeric-aware) so
//      the user's intentional CoA numbering is preserved.
//
// Usage:
//   const options = buildAccountOptions(
//     accounts,                                       // FULL list (don't pre-filter)
//     (a) => a.isActive && a.allowDirectPosting,     // visibility predicate
//   );
//   {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}

import type { Account } from '@accounting/shared';

export interface AccountOption {
  id: string;
  code: string;
  name: string;
  /** "GrandParent:Parent:Child" — single name for top-level accounts. */
  fullPath: string;
  /** "<code> — <fullPath>" — ready for direct use as the <option> label. */
  label: string;
  /** The underlying account, for callers that need more than the label. */
  account: Account;
}

export function buildAccountOptions(
  allAccounts: Account[],
  predicate: (a: Account) => boolean = () => true,
): AccountOption[] {
  if (allAccounts.length === 0) return [];

  const byId = new Map(allAccounts.map((a) => [a.id, a]));

  // Walk up the (possibly invisible) parent chain to build "Parent:Child".
  // Cycle-safe — parentAccountId is self-referential with no FK in the schema.
  const fullPath = (a: Account): string => {
    const segs = [a.name];
    const seen = new Set<string>([a.id]);
    let pid = a.parentAccountId;
    while (pid && !seen.has(pid)) {
      const p = byId.get(pid);
      if (!p) break;
      segs.unshift(p.name);
      seen.add(pid);
      pid = p.parentAccountId;
    }
    return segs.join(':');
  };

  // Group children by their parent id. Accounts whose parent isn't in the list
  // are treated as roots (orphans surface at the top level rather than vanish).
  const childrenByParent = new Map<string | null, Account[]>();
  for (const a of allAccounts) {
    const key = a.parentAccountId && byId.has(a.parentAccountId) ? a.parentAccountId : null;
    const arr = childrenByParent.get(key);
    if (arr) arr.push(a);
    else childrenByParent.set(key, [a]);
  }
  // Numeric-aware code sort so 9 < 10 < 100 within siblings.
  for (const arr of childrenByParent.values()) {
    arr.sort((x, y) => x.code.localeCompare(y.code, undefined, { numeric: true }));
  }

  const out: AccountOption[] = [];
  const walk = (parentKey: string | null) => {
    for (const a of childrenByParent.get(parentKey) ?? []) {
      if (predicate(a)) {
        const path = fullPath(a);
        out.push({
          id: a.id,
          code: a.code,
          name: a.name,
          fullPath: path,
          label: `${a.code} — ${path}`,
          account: a,
        });
      }
      // Recurse into children regardless of whether the parent itself passes
      // the predicate — header accounts are typically excluded but their
      // postable children still need to appear under them.
      walk(a.id);
    }
  };
  walk(null);
  return out;
}
